import {defineRecipe} from '@chakra-ui/react3'

const chakraButtonRecipe = defineRecipe({
    className: 'chakra-button',
    base: {
        display: 'inline-flex',
        appearance: 'none',
        alignItems: 'center',
        justifyContent: 'center',
        userSelect: 'none',
        position: 'relative',
        borderRadius: 'l2',
        whiteSpace: 'nowrap',
        verticalAlign: 'middle',
        borderWidth: '1px',
        borderColor: 'transparent',
        cursor: 'button',
        flexShrink: '0',
        outline: '0',
        lineHeight: '1.2',
        isolation: 'isolate',
        fontWeight: 'medium',
        transitionProperty: 'common',
        transitionDuration: 'moderate',
        focusVisibleRing: 'outside',
        _disabled: {
            layerStyle: 'disabled'
        },
        _icon: {
            flexShrink: '0'
        }
    },

    variants: {
        size: {
            '2xs': {
                h: '6',
                minW: '6',
                textStyle: 'xs',
                px: '2',
                gap: '1',
                _icon: {
                    width: '3.5',
                    height: '3.5'
                }
            },
            xs: {
                h: '8',
                minW: '8',
                textStyle: 'xs',
                px: '2.5',
                gap: '1',
                _icon: {
                    width: '4',
                    height: '4'
                }
            },
            sm: {
                h: '9',
                minW: '9',
                px: '3.5',
                textStyle: 'sm',
                gap: '2',
                _icon: {
                    width: '4',
                    height: '4'
                }
            },
            md: {
                h: '10',
                minW: '10',
                textStyle: 'sm',
                px: '4',
                gap: '2',
                _icon: {
                    width: '5',
                    height: '5'
                }
            },
            lg: {
                h: '11',
                minW: '11',
                textStyle: 'md',
                px: '5',
                gap: '3',
                _icon: {
                    width: '5',
                    height: '5'
                }
            },
            xl: {
                h: '12',
                minW: '12',
                textStyle: 'md',
                px: '5',
                gap: '2.5',
                _icon: {
                    width: '5',
                    height: '5'
                }
            },
            '2xl': {
                h: '16',
                minW: '16',
                textStyle: 'lg',
                px: '7',
                gap: '3',
                _icon: {
                    width: '6',
                    height: '6'
                }
            }
        },

        variant: {
            solid: {
                bg: 'colorPalette.solid',
                color: 'colorPalette.contrast',
                borderColor: 'transparent',
                _hover: {
                    bg: 'colorPalette.solid/90'
                },
                _expanded: {
                    bg: 'colorPalette.solid/90'
                }
            },

            subtle: {
                bg: 'colorPalette.subtle',
                color: 'colorPalette.fg',
                borderColor: 'transparent',
                _hover: {
                    bg: 'colorPalette.muted'
                },
                _expanded: {
                    bg: 'colorPalette.muted'
                }
            },

            surface: {
                bg: 'colorPalette.subtle',
                color: 'colorPalette.fg',
                shadow: '0 0 0px 1px var(--shadow-color)',
                shadowColor: 'colorPalette.muted',
                _hover: {
                    bg: 'colorPalette.muted'
                },
                _expanded: {
                    bg: 'colorPalette.muted'
                }
            },

            outline: {
                borderWidth: '1px',
                borderColor: 'colorPalette.muted',
                color: 'colorPalette.fg',
                _hover: {
                    bg: 'colorPalette.subtle'
                },
                _expanded: {
                    bg: 'colorPalette.subtle'
                }
            },

            ghost: {
                bg: 'transparent',
                color: 'colorPalette.fg',
                _hover: {
                    bg: 'colorPalette.subtle'
                },
                _expanded: {
                    bg: 'colorPalette.subtle'
                }
            },

            plain: {
                color: 'colorPalette.fg'
            }
        }
    },

    defaultVariants: {
        size: 'md',
        variant: 'solid'
    }
})

export const buttonRecipe = defineRecipe({
    ...chakraButtonRecipe,
    // prevents chakra2 focus indicator from applying
    className: 'leap-button',
    base: {
        ...chakraButtonRecipe.base,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '2',
        width: 'fit-content',
        borderWidth: 0,
        borderRadius: 'round',
        fontWeight: null,
        lineHeight: null,
        transitionProperty: 'common',
        transitionDuration: 'normal',
        appearance: 'none',
        _focusVisible: {
            outlineOffset: '2px',
            outlineStyle: 'solid',
            outlineWidth: '1px'
        },
        _disabled: {
            opacity: 0.4,
            cursor: 'not-allowed'
        }
    },
    variants: {
        variant: {
            ...chakraButtonRecipe.variants.variant,
            primaryFilled: {
                bg: 'background.actions.primary',
                color: 'text.on-surface',
                iconColor: 'icons.on-color',
                _hover: {bg: 'background.actions.primary-hover'},
                _focusVisible: {
                    outlineColor: 'border.brand'
                }
            },
            primaryOutline: {
                bg: 'transparent',
                color: 'text.brand-default',
                iconColor: 'icons.brand',
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: 'border.brand',
                _hover: {
                    bg: 'background.actions.primary',
                    color: 'text.on-surface',
                    iconColor: 'icons.on-color'
                },
                _focusVisible: {
                    outlineColor: 'border.brand'
                }
            },
            secondaryFilled: {
                bg: 'background.actions.neutral',
                color: 'text.primary-inverted',
                iconColor: 'icons.primary-inverted',
                _hover: {bg: 'background.actions.neutral-hover'},
                _focusVisible: {
                    outlineColor: 'border.strong'
                }
            },
            secondaryOutline: {
                bg: 'transparent',
                color: 'text.primary',
                iconColor: 'icons.primary',
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: 'border.strong',
                _hover: {
                    bg: 'background.actions.neutral-hover',
                    color: 'text.primary-inverted',
                    iconColor: 'icons.primary-inverted'
                },
                _focusVisible: {
                    outlineColor: 'border.strong'
                }
            },
            secondaryGhost: {
                bg: 'background.actions.ghost',
                color: 'text.primary',
                iconColor: 'icons.primary',
                _hover: {
                    bg: 'background.actions.ghost-hover'
                },
                _focusVisible: {
                    outlineColor: 'border.strong'
                }
            },
            successFilled: {
                bg: 'background.system.success-strong',
                color: 'text.on-surface',
                iconColor: 'icons.on-color',
                _hover: {
                    bg: 'background.system.success-strong-hover'
                },
                _focusVisible: {
                    outlineColor: 'border.system.success'
                }
            },
            successOutline: {
                bg: 'transparent',
                color: 'text.system.success',
                iconColor: 'icons.system.success',
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: 'border.system.success',
                _hover: {
                    bg: 'background.system.success-strong',
                    color: 'text.on-surface',
                    iconColor: 'icons.on-color'
                },
                _focusVisible: {
                    outlineColor: 'border.system.success'
                }
            },
            successGhost: {
                bg: 'background.system.success-light',
                color: 'text.system.success',
                iconColor: 'icons.system.success',
                _hover: {
                    bg: 'background.system.success-subtle'
                },
                _focusVisible: {
                    outlineColor: 'border.system.success'
                }
            },
            errorFilled: {
                bg: 'background.system.error-strong',
                color: 'text.on-surface',
                iconColor: 'icons.on-color',
                _hover: {
                    bg: 'background.system.error-strong-hover'
                },
                _focusVisible: {
                    outlineColor: 'border.system.error'
                }
            },
            errorOutline: {
                bg: 'transparent',
                color: 'text.system.error',
                iconColor: 'icons.system.error',
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: 'border.system.error',
                _hover: {
                    bg: 'background.system.error-strong',
                    color: 'text.on-surface',
                    iconColor: 'icons.on-color'
                },
                _focusVisible: {
                    outlineColor: 'border.system.error'
                }
            },
            errorGhost: {
                bg: 'background.system.error-light',
                color: 'text.system.error',
                iconColor: 'icons.system.error',
                _hover: {
                    bg: 'background.system.error-subtle'
                },
                _focusVisible: {
                    outlineColor: 'border.system.error'
                }
            }
        },
        size: {
            sm: {
                ...chakraButtonRecipe.variants.size.sm,
                minW: null,
                paddingTop: 1,
                paddingBottom: 1,
                paddingLeft: 2,
                paddingRight: 2,
                iconSpacing: 1,
                textStyle: 'heading.2xsmall'
            },
            md: {
                ...chakraButtonRecipe.variants.size.md,
                minW: null,
                paddingTop: 2,
                paddingBottom: 2,
                paddingLeft: 3,
                paddingRight: 3,
                iconSpacing: 1.5,
                textStyle: 'heading.2xsmall'
            },
            lg: {
                ...chakraButtonRecipe.variants.size.lg,
                minW: null,
                paddingTop: 2.5,
                paddingBottom: 2.5,
                paddingLeft: 4,
                paddingRight: 4,
                iconSpacing: 2,
                textStyle: 'body.medium'
            }
        }
    }
})
